import { Head, Link, router } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"

interface ReleaseRow {
  id: number
  version: string
  published: boolean
  current: boolean
  released_at: string | null
  created_at: string
  zip_filename: string
  dmg_filename: string | null
  zip_size: number
}

export default function AdminReleasesIndex({ releases }: { releases: ReleaseRow[] }) {
  return (
    <>
      <Head title="Releases">
        <meta name="description" content="Upload and publish Flywrite desktop app releases." />
        <meta property="og:title" content="Releases" />
        <meta property="og:description" content="Upload and publish Flywrite desktop app releases." />
      </Head>
      <AdminShell>
        <PageHeader
          title="Releases"
          description="Desktop app builds stored in R2. Publishing puts a release on the auto-update feed."
          actions={
            <Button size="sm" asChild>
              <Link href="/admin/releases/new">Upload release</Link>
            </Button>
          }
        />

        {releases.length === 0 ? (
          <p className="mt-8 text-sm text-ink-muted">
            No releases yet. <Link href="/admin/releases/new">Upload the first one</Link>. See{" "}
            <Link href="/admin/docs/shipping-a-release">Shipping a release</Link> for the full
            workflow.
          </p>
        ) : (
          <div className="mt-6 overflow-x-auto rounded-lg border border-hairline">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hairline bg-surface text-left">
                  <th className="px-4 py-3 font-medium text-ink-muted">Version</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Status</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Released</th>
                  <th className="px-4 py-3 text-right font-medium text-ink-muted">Size</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Files</th>
                  <th className="px-4 py-3 text-right font-medium text-ink-muted"></th>
                </tr>
              </thead>
              <tbody>
                {releases.map((release) => (
                  <tr key={release.id} className="border-b border-hairline last:border-0">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <code className="font-mono text-xs">{release.version}</code>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {release.published ? (
                        <Badge tone="accent">Published</Badge>
                      ) : (
                        <Badge tone="muted">Draft</Badge>
                      )}
                      {release.current && (
                        <span className="ml-2 text-xs text-ink-muted">current</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {release.released_at ? formatDate(release.released_at) : "—"}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {formatBytes(release.zip_size)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-ink-muted">
                        {release.zip_filename}
                        {release.dmg_filename ? ` · ${release.dmg_filename}` : ""}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {!release.published && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() =>
                            router.post(`/admin/releases/${release.id}/publish`, {}, { preserveScroll: true })
                          }
                        >
                          Publish
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminShell>
    </>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

function formatBytes(bytes: number) {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${bytes} B`
}
